// Typed client for the indexer API (docs/API.md). Amounts are decimal strings of base units.
import { API_URL } from '../config/env';
import { cleanLabel, cleanText, safeImage, safeLink } from './sanitize';

export type Phase = 'scheduled' | 'active' | 'graduated';
export type LaunchView =
  | 'new'
  | 'hot-work'
  | 'lowest-difficulty'
  | 'rising-difficulty'
  | 'near-graduation'
  | 'scheduled'
  | 'graduated'
  | 'all';

export interface LaunchMetadata {
  description?: string;
  image?: string;
  links?: { x?: string; site?: string; telegram?: string };
}

export interface LaunchSummary {
  address: string;
  token: string;
  creator: string;
  name: string;
  symbol: string;
  metadataURI: string;
  metadata: LaunchMetadata | null;
  index: number;
  createdAt: number;
  createdBlock: number;
  openAt: number;
  phase: Phase;
  /** challenge #1 has opened (at creation, or via ignite() for scheduled launches) */
  ignited: boolean;
  /** past openAt and not ignited: anyone may open the forge */
  readyToIgnite: boolean;
  sold: string;
  reserve: string;
  progress: number;
  lotsForged: number;
  lotsInCirculation: number;
  spotPrice: string;
  spotPriceFloat: number;
  marketCapFloat: number;
  /** null until ignited */
  challengeId: number | null;
  /** null until ignited and after graduation */
  openTarget: string | null;
  challengeOpenedAt: number | null;
  difficultyFloat: number | null;
  difficultyChange: number | null;
  estNetworkHashrateFloat: number | null;
  forges10m: number;
  lastForgeAt: number | null;
  uniqueMiners: number;
  topMinerShare: number | null;
  holders: number;
  buys: number;
  sells: number;
  volume: string;
  creatorFees: string;
  protocolFees: string;
  graduatedAt: number | null;
  pair: string;
  poolReserveToken: string | null;
  poolReserveWeth: string | null;
}

export interface LaunchDetail extends LaunchSummary {
  lp?: { principal: string | null; collected: unknown[] };
}

export type FeedType =
  | 'launch'
  | 'forge'
  | 'sell'
  | 'challenge'
  | 'graduated'
  | 'lp-fees'
  | 'creator-fees'
  | 'protocol-fees'
  | 'creator-changed'
  | 'creator-handover';

export const FEED_TYPES: readonly FeedType[] = [
  'launch',
  'forge',
  'sell',
  'challenge',
  'graduated',
  'lp-fees',
  'creator-fees',
  'protocol-fees',
  'creator-changed',
  'creator-handover',
];

export interface FeedItem {
  id: string;
  type: FeedType;
  launch: string;
  symbol: string;
  blockNumber: number;
  logIndex: number;
  txHash: string;
  timestamp: number;
  actor?: string;
  challengeId?: number;
  nonce?: string;
  work?: string;
  targetMet?: string;
  difficultyMetFloat?: number;
  age?: number;
  amount?: string;
  cost?: string;
  fee?: string;
  payout?: string;
  sold?: string;
  reserve?: string;
  openTarget?: string;
  prevOpenTarget?: string;
  difficultyFloat?: number;
  prevDifficultyFloat?: number;
  challengeBase?: string;
  challenge?: string;
  pair?: string;
  tokenAmount?: string;
  quoteAmount?: string;
  liquidityBurned?: string;
  previousCreator?: string;
  pendingCreator?: string;
}

export interface WorkItem {
  challengeId: number;
  miner: string;
  targetMet: string;
  difficultyMetFloat: number;
  openTarget: string;
  age: number;
  timestamp: number;
  txHash: string;
}

export interface MinerRow {
  address: string;
  proofs: number;
  share: number;
  lastAt: number;
}

export interface HolderRow {
  address: string;
  balance: string;
  share: number;
}

export interface Candle {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export interface Portfolio {
  holdings: { launch: LaunchSummary; balance: string }[];
  created: { launch: LaunchSummary; creatorFeesAccrued: string }[];
}

export interface Stats {
  launches: number;
  active: number;
  scheduled: number;
  graduated: number;
  forges24h: number;
  volume24h: string;
  holdersTotal: number;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function get<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { headers: { accept: 'application/json' } });
  } catch {
    throw new ApiError(`Indexer unreachable at ${API_URL}`, 0);
  }
  if (!res.ok) throw new ApiError(`Indexer returned ${res.status} for ${path}`, res.status);
  return (await res.json()) as T;
}

const qs = (p: Record<string, string | number | undefined>) => {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
};

const lc = (a: string) => a.toLowerCase();

function cleanMeta(m: LaunchMetadata | null | undefined): LaunchMetadata | null {
  if (!m) return null;
  return {
    description: m.description === undefined ? undefined : cleanText(m.description),
    image: safeImage(m.image) ?? undefined,
    links: m.links ? { x: safeLink(m.links.x) ?? undefined, site: safeLink(m.links.site) ?? undefined, telegram: safeLink(m.links.telegram) ?? undefined } : undefined,
  };
}

/** Creator-controlled fields are sanitized once, here at the boundary (HC-15). */
export const cleanLaunch = <T extends LaunchSummary>(l: T): T => ({ ...l, name: cleanLabel(l.name), symbol: cleanLabel(l.symbol), metadata: cleanMeta(l.metadata) });
export const cleanFeed = (i: FeedItem): FeedItem => ({ ...i, symbol: cleanLabel(i.symbol) });

export const api = {
  stats: () => get<Stats>('/api/stats'),
  launches: (p: { view?: LaunchView; limit?: number; offset?: number; q?: string }) =>
    get<{ items: LaunchSummary[]; total: number }>(`/api/launches${qs(p)}`).then((r) => ({ ...r, items: r.items.map(cleanLaunch) })),
  launch: (address: string) => get<LaunchDetail>(`/api/launches/${lc(address)}`).then(cleanLaunch),
  events: (address: string, p: { limit?: number; before?: string } = {}) =>
    get<{ items: FeedItem[]; nextCursor: string | null }>(`/api/launches/${lc(address)}/events${qs(p)}`).then((r) => ({ ...r, items: r.items.map(cleanFeed) })),
  candles: (address: string, interval = 60) => get<{ items: Candle[] }>(`/api/launches/${lc(address)}/candles${qs({ interval })}`),
  work: (address: string, limit = 200) => get<{ items: WorkItem[] }>(`/api/launches/${lc(address)}/work${qs({ limit })}`),
  miners: (address: string, limit = 20) => get<{ items: MinerRow[] }>(`/api/launches/${lc(address)}/miners${qs({ limit })}`),
  holders: (address: string, limit = 50) =>
    get<{ items: HolderRow[]; total: number }>(`/api/launches/${lc(address)}/holders${qs({ limit })}`),
  portfolio: (wallet: string) =>
    get<Portfolio>(`/api/portfolio/${lc(wallet)}`).then((r) => ({
      holdings: r.holdings.map((h) => ({ ...h, launch: cleanLaunch(h.launch) })),
      created: r.created.map((c) => ({ ...c, launch: cleanLaunch(c.launch) })),
    })),
  recent: (limit = 30) => get<{ items: FeedItem[] }>(`/api/events/recent${qs({ limit })}`).then((r) => ({ items: r.items.map(cleanFeed) })),
  streamUrl: (launch?: string) => `${API_URL}/api/events/stream${qs({ launch: launch ? lc(launch) : undefined })}`,

  async uploadMetadata(form: FormData): Promise<{ uri: string; image: string; sha256: string }> {
    let res: Response;
    try {
      res = await fetch(`${API_URL}/api/metadata`, { method: 'POST', body: form });
    } catch {
      throw new ApiError(`Indexer unreachable at ${API_URL}`, 0);
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let message = text.slice(0, 200);
      try {
        message = (JSON.parse(text) as { error?: string }).error ?? message;
      } catch {
        /* plain-text error */
      }
      throw new ApiError(message || `Metadata upload failed (${res.status})`, res.status);
    }
    return (await res.json()) as { uri: string; image: string; sha256: string };
  },
};

/**
 * Retry policy for per-launch history: a launch created moments ago returns 404 until the indexer
 * catches up, and an unreachable indexer may come back, so keep trying for about half a minute.
 */
export const indexerRetry = {
  retry: (failures: number, e: unknown) => failures < 12 && (!(e instanceof ApiError) || e.status === 404 || e.status === 0 || e.status >= 500),
  retryDelay: 2_500,
};

export const notIndexedYet = (e: unknown) => e instanceof ApiError && e.status === 404;

